import React, { useState, useRef, useEffect, useCallback } from "react";
import { FiChevronDown, FiSearch, FiEyeOff, FiArrowUp, FiArrowDown, FiFilter, FiCheck } from "react-icons/fi";
import { reservations as resApi } from "../services/api";

const FILTERABLE_COLS = [
  "operation_type","operation_status","client_type","unit_type","readiness",
  "deposit_status","bank_account","commission_status_res","commission_status_acc",
  "developer","marketer","client_name"
];

export default function ColumnHeaderMenu({ label, colKey, sortBy, sortDir, onSort, onFilter, onHide, activeFilter, onMoveLeft, onMoveRight, getFilterParams }) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [allValues, setAllValues] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dropLeft, setDropLeft] = useState(false);
  // Multi-select: track selected values as array
  const [selectedValues, setSelectedValues] = useState([]);
  const ref = useRef();

  // Parse activeFilter into array when component mounts or filter changes
  useEffect(() => {
    if (activeFilter) {
      // activeFilter can be a ||| separated string for multi-select
      const vals = activeFilter.split("|||").filter(v => v);
      setSelectedValues(vals);
    } else {
      setSelectedValues([]);
    }
  }, [activeFilter]);

  useEffect(() => {
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

  // جلب القيم عند الفتح
  const handleOpen = useCallback(async () => {
    const willOpen = !open;
    if (willOpen && ref.current) {
      const rect = ref.current.getBoundingClientRect();
      setDropLeft(rect.left < window.innerWidth / 2);
    }
    setOpen(willOpen);
    setSearch("");
    // v3.3.999 — تسلسل إكسل: إعادة الجلب عند كل فتح بالقيم المتبقية بعد فلاتر الشبكة الأخرى (فلتر العمود نفسه مستثنى)
    if (willOpen && FILTERABLE_COLS.includes(colKey)) {
      setLoading(true);
      try {
        const p = getFilterParams ? { ...getFilterParams() } : {};
        delete p[colKey];
        const r = await resApi.distinctValues(colKey, p);
        setAllValues(r.data.values || []);
      } catch(e) { /* ignore */ }
      setLoading(false);
    }
  }, [open, colKey, getFilterParams]);

  const isActive = sortBy === colKey;
  const hasFilter = selectedValues.length > 0;

  const filtered = allValues.filter(v =>
    v && v.toString().toLowerCase().includes(search.toLowerCase())
  );

  // Toggle a value in the selection
  const toggleValue = (val) => {
    setSelectedValues(prev => {
      if (prev.includes(val)) {
        return prev.filter(v => v !== val);
      } else {
        return [...prev, val];
      }
    });
  };

  // Apply filter
  const applyFilter = () => {
    if (selectedValues.length === 0) {
      onFilter(colKey, "");
    } else {
      // Join with ||| delimiter for backend parsing
      onFilter(colKey, selectedValues.join("|||"));
    }
    setOpen(false);
  };

  // Clear all selections
  const clearFilter = () => {
    setSelectedValues([]);
    onFilter(colKey, "");
    setOpen(false);
  };

  // Select all visible items
  const selectAll = () => {
    setSelectedValues(filtered.map(v => v.toString()));
  };

  return (
    <th ref={ref} style={{ position: "relative", cursor: "pointer", userSelect: "none" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 3 }} onClick={handleOpen}>
        <span>{label}</span>
        {isActive && (sortDir === "ASC" ? <FiArrowUp size={10} /> : <FiArrowDown size={10} />)}
        {hasFilter
          ? <FiFilter size={10} style={{ color: "#2563eb", opacity: 1 }} />
          : <FiChevronDown size={10} style={{ opacity: 0.4 }} />
        }
        {hasFilter && (
          <span style={{ 
            minWidth: 16, height: 16, borderRadius: "50%", background: "#2563eb", 
            display: "inline-flex", alignItems: "center", justifyContent: "center",
            color: "#fff", fontSize: 9, fontWeight: 700, flexShrink: 0 
          }}>
            {selectedValues.length}
          </span>
        )}
      </div>

      {open && (
        <div style={{
          position: "fixed",
          top: ref.current ? ref.current.getBoundingClientRect().bottom + 2 : 0,
          ...(dropLeft
            ? { left: ref.current ? ref.current.getBoundingClientRect().left : 0 }
            : { right: ref.current ? window.innerWidth - ref.current.getBoundingClientRect().right : 0 }
          ),
          zIndex: 99999,
          background: "#fff", border: "1px solid #e2e8f0", borderRadius: 8,
          boxShadow: "0 8px 24px rgba(0,0,0,0.15)", padding: 8, minWidth: 220,
          direction: "rtl",
        }}>
          {/* Sort */}
          <button onClick={() => { onSort(colKey, "ASC"); setOpen(false); }} style={menuBtn}>
            <FiArrowUp size={12} /> ترتيب تصاعدي
          </button>
          <button onClick={() => { onSort(colKey, "DESC"); setOpen(false); }} style={menuBtn}>
            <FiArrowDown size={12} /> ترتيب تنازلي
          </button>

          {FILTERABLE_COLS.includes(colKey) && (
            <>
              <div style={{ borderTop: "1px solid #f1f5f9", margin: "4px 0" }} />

              {/* Search in column */}
              <div style={{ display: "flex", alignItems: "center", gap: 4, padding: "5px 8px", background: "#f8fafc", borderRadius: 5, marginBottom: 4 }}>
                <FiSearch size={12} color="#94a3b8" />
                <input
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                  onClick={e => e.stopPropagation()}
                  placeholder="بحث في القيم..."
                  style={{ border: "none", outline: "none", background: "none", width: "100%", fontFamily: "inherit", fontSize: 12, padding: 2 }}
                />
              </div>

              {/* Selection controls */}
              <div style={{ display: "flex", gap: 6, marginBottom: 4, padding: "0 4px" }}>
                <button onClick={selectAll} style={{ ...smallBtn, color: "#2563eb" }}>تحديد الكل</button>
                <button onClick={() => setSelectedValues([])} style={{ ...smallBtn, color: "#64748b" }}>إلغاء الكل</button>
              </div>

              {/* Filter values with checkboxes */}
              <div style={{ maxHeight: 220, overflowY: "auto", borderTop: "1px solid #f1f5f9", paddingTop: 4 }}>
                {loading && (
                  <div style={{ padding: "8px 10px", color: "#94a3b8", fontSize: 12 }}>جاري التحميل...</div>
                )}
                {!loading && filtered.length === 0 && (
                  <div style={{ padding: "8px 10px", color: "#94a3b8", fontSize: 12 }}>لا توجد نتائج</div>
                )}
                {!loading && filtered.map(v => {
                  const isSelected = selectedValues.includes(v.toString());
                  return (
                    <label key={v} style={{ 
                      display: "flex", alignItems: "center", gap: 8, 
                      padding: "6px 10px", cursor: "pointer", borderRadius: 4,
                      background: isSelected ? "#eff6ff" : "transparent",
                      transition: "background 0.1s"
                    }}
                    onMouseEnter={e => { if (!isSelected) e.currentTarget.style.background = "#f8fafc"; }}
                    onMouseLeave={e => { if (!isSelected) e.currentTarget.style.background = "transparent"; }}
                    >
                      <div style={{
                        width: 16, height: 16, borderRadius: 3,
                        border: isSelected ? "none" : "1.5px solid #cbd5e1",
                        background: isSelected ? "#2563eb" : "#fff",
                        display: "flex", alignItems: "center", justifyContent: "center",
                        flexShrink: 0, transition: "all 0.1s"
                      }}>
                        {isSelected && <FiCheck size={11} color="#fff" strokeWidth={3} />}
                      </div>
                      <input 
                        type="checkbox" 
                        checked={isSelected}
                        onChange={() => toggleValue(v.toString())}
                        style={{ display: "none" }}
                      />
                      <span style={{ 
                        fontSize: 12, color: isSelected ? "#1e40af" : "#1e293b",
                        fontWeight: isSelected ? 600 : 400
                      }}>{v}</span>
                    </label>
                  );
                })}
              </div>

              {/* Apply / Clear buttons */}
              <div style={{ borderTop: "1px solid #f1f5f9", marginTop: 4, paddingTop: 8, display: "flex", gap: 6 }}>
                <button onClick={applyFilter} 
                  style={{ 
                    flex: 1, padding: "7px 12px", border: "none", borderRadius: 6,
                    background: "#2563eb", color: "#fff", fontSize: 12, fontWeight: 600,
                    cursor: "pointer", fontFamily: "inherit"
                  }}>
                  تطبيق {selectedValues.length > 0 && `(${selectedValues.length})`}
                </button>
                {hasFilter && (
                  <button onClick={clearFilter}
                    style={{ 
                      padding: "7px 12px", border: "1px solid #fca5a5", borderRadius: 6,
                      background: "#fff", color: "#ef4444", fontSize: 12, fontWeight: 600,
                      cursor: "pointer", fontFamily: "inherit"
                    }}>
                    مسح
                  </button>
                )}
              </div>
            </>
          )}

          <div style={{ borderTop: "1px solid #f1f5f9", margin: "4px 0" }} />
          {onMoveRight && <button onClick={() => { onMoveRight(colKey); setOpen(false); }} style={menuBtn}>
            ◀ نقل يمين
          </button>}
          {onMoveLeft && <button onClick={() => { onMoveLeft(colKey); setOpen(false); }} style={menuBtn}>
            ▶ نقل يسار
          </button>}
          <button onClick={() => { onHide(colKey); setOpen(false); }} style={{ ...menuBtn, color: "#ef4444" }}>
            <FiEyeOff size={12} /> إخفاء العمود
          </button>
        </div>
      )}
    </th>
  );
}

const menuBtn = {
  display: "flex", alignItems: "center", gap: 6, width: "100%",
  padding: "6px 10px", border: "none", background: "none",
  fontFamily: "inherit", fontSize: 12, cursor: "pointer", borderRadius: 4,
  textAlign: "right", color: "#1e293b",
};

const smallBtn = {
  padding: "3px 8px", border: "none", background: "none",
  fontFamily: "inherit", fontSize: 11, cursor: "pointer", borderRadius: 3,
  fontWeight: 600,
};
